import { computeWeightedComplexity, getComplexityFactors, type BuildIntegrationCategory, type DataFrequency } from './common';

export function estimateBuildTimeAndCost(
    data: {
        categories: BuildIntegrationCategory[];
        numberOfNewIntegrations: number;
        yearsInBusiness: number;
        developersAnnualSalary: number;
        productManagersAnnualSalary: number;
        numberOfExistingIntegrations: number;
        // optional inputs for extended model
        baseBuildWeeks?: number; // default 12 weeks
        legacySystems?: boolean; // derived from years in business if not provided
        dataFrequency?: DataFrequency; // default 'batch'
        partnerFeesBuild?: number; // default 0
    },
    debug = false
) {
    if (debug) {
        console.log('[BUILD] data', JSON.stringify(data, null, 2));
    }
    if (data.numberOfNewIntegrations <= 0) {
        return {
            monthsForEachIntegration: 0,
            costForEachIntegration: 0,
            monthsForAllIntegrations: 0,
            costForAllIntegrations: 0,
        };
    }

    const numberOfIntegrations = data.numberOfNewIntegrations + data.numberOfExistingIntegrations;

    const weeksPerIntegration = estimateTimeToBuildANewIntegrationInWeeks(
        {
            yearsInBusiness: data.yearsInBusiness,
            numberOfExistingIntegrations: data.numberOfExistingIntegrations,
            categories: data.categories,
            baseBuildWeeks: data.baseBuildWeeks ?? 12,
            legacySystems: data.legacySystems,
            dataFrequency: data.dataFrequency ?? 'batch',
        },
        debug
    );
    const monthsForEachIntegration = weeksPerIntegration / 4;
    if (debug) {
        console.log('[BUILD] monthsForEachIntegration', monthsForEachIntegration);
    }
    const costOfDevelopersPerMonth = (1 * monthsForEachIntegration * data.developersAnnualSalary) / 12;
    if (debug) {
        console.log('[BUILD] costOfDevelopers', costOfDevelopersPerMonth);
    }

    // 1 product manager for 6 devs
    const PM_DEV = 6;
    const costOfProductManagerPerMonth = ((1 / PM_DEV) * monthsForEachIntegration * data.productManagersAnnualSalary) / 12;
    if (debug) {
        console.log('[BUILD] costOfProductManager', costOfProductManagerPerMonth);
    }

    const costForEachIntegration = costOfDevelopersPerMonth + costOfProductManagerPerMonth;
    if (debug) {
        console.log('[BUILD] costForEachIntegration', costForEachIntegration);
    }
    const monthsForAllIntegrations = monthsForEachIntegration * data.numberOfNewIntegrations;
    if (debug) {
        console.log('[BUILD] monthsForAllIntegrations', monthsForAllIntegrations);
    }
    const partnerFeesBuild = data.partnerFeesBuild ?? 0;
    const costForAllIntegrations = costForEachIntegration * data.numberOfNewIntegrations + partnerFeesBuild;
    if (debug) {
        console.log('[BUILD] costForAllIntegrations', costForAllIntegrations);
    }

    return {
        monthsForEachIntegration: Number(monthsForEachIntegration.toFixed(1)),
        costForEachIntegration: Number(costForEachIntegration.toFixed(0)),
        monthsForAllIntegrations: Number(monthsForAllIntegrations.toFixed(1)),
        costForAllIntegrations: Number(costForAllIntegrations.toFixed(0)),
    };
}

function estimateTimeToBuildANewIntegrationInWeeks(
    data: {
        yearsInBusiness: number;
        numberOfExistingIntegrations: number;
        categories: BuildIntegrationCategory[];
        baseBuildWeeks: number;
        legacySystems?: boolean;
        dataFrequency: DataFrequency;
    },
    debug = false
) {
    if (debug) {
        console.log('[BUILD-ESTIMATE] yearsInBusiness', data.yearsInBusiness);
        console.log('[BUILD-ESTIMATE] numberOfExistingIntegrations', data.numberOfExistingIntegrations);
        console.log('[BUILD-ESTIMATE] categories', data.categories);
    }

    // Weighted complexity factor using average complexity (1..4) scaled to ~1..2
    const complexityFactor = computeWeightedComplexity(data.categories, 'build');
    if (debug) {
        console.log('[BUILD-ESTIMATE] complexityFactor', complexityFactor);
    }

    // Legacy factor: infer from years if not explicitly provided
    // <=2y modern stack (1.0), 2-4y (1.1), 4-8y (1.2), >8y (1.3)
    const inferredLegacy = data.legacySystems ?? data.yearsInBusiness > 4;
    const legacyFactor = inferredLegacy ? (data.yearsInBusiness > 8 ? 1.3 : 1.2) : data.yearsInBusiness > 2 ? 1.1 : 1.0;
    if (debug) {
        console.log('[BUILD-ESTIMATE] legacyFactor', legacyFactor);
    }

    // Learning curve: more existing integrations reduce build time (log-based)
    const learningFactor = 1 / (1 + Math.log(1 + data.numberOfExistingIntegrations));
    if (debug) {
        console.log('[BUILD-ESTIMATE] learningFactor', learningFactor);
    }

    // Data frequency factor
    const realtimeFactor = data.dataFrequency === 'real-time' ? 1.2 : data.dataFrequency === 'near-real-time' ? 1.1 : 1.0;
    if (debug) {
        console.log('[BUILD-ESTIMATE] realtimeFactor', realtimeFactor);
    }

    const weeks = data.baseBuildWeeks * complexityFactor * legacyFactor * realtimeFactor * learningFactor;
    // Do not go below half the base time
    const minWeeks = data.baseBuildWeeks * 0.5;
    return Math.max(weeks, minWeeks);
}
